import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, PackageSearch } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { shopApi, fmt, formatApiError, STATUS_LABELS } from "../../lib/shopApi";

export const StatusTimeline = ({ order }) => {
  const timeline = order.timeline || [];
  return (
    <div className="space-y-0" data-testid="order-timeline">
      {timeline.map((t, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <span className={`w-3.5 h-3.5 rounded-full border-2 ${i === timeline.length - 1 ? "bg-[#C9A24B] border-[#C9A24B]" : "border-[#C9A24B]/50 bg-transparent"}`} />
            {i < timeline.length - 1 && <span className="w-px flex-1 bg-[#C9A24B]/20" />}
          </div>
          <div className="pb-6 -mt-0.5">
            <p className="text-sm font-semibold text-[#F5EFE3]">{STATUS_LABELS[t.status] || t.status.replace(/_/g, " ")}</p>
            <p className="text-[#F5EFE3]/40 text-xs mt-0.5">
              {t.at ? new Date(t.at).toLocaleString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export const OrderItemsList = ({ items }) => (
  <div className="space-y-2">
    {items.map((i, idx) => (
      <div key={idx} className="flex justify-between text-sm">
        <span className="text-[#F5EFE3]/70">
          {i.name}{i.size_label ? ` (${i.size_label})` : ""} × {i.qty}
          {Object.values(i.options || {}).length > 0 && (
            <span className="block text-[#F5EFE3]/45 text-xs mt-0.5">
              {[i.options.length, i.options.color, i.options.thickness].filter(Boolean).join(" · ")}
            </span>
          )}
        </span>
        {i.line_total_cents != null && <span>{fmt(i.line_total_cents)}</span>}
      </div>
    ))}
  </div>
);

const TrackOrderPage = () => {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    setError("");
    setOrder(null);
    setBusy(true);
    try {
      const r = await shopApi.post("/shop/track", { order_number: orderNumber, email });
      setOrder(r.data);
    } catch (err) {
      setError(formatApiError(err, "No order found with that number and email"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ShopLayout title="Track Your Order | Loctician Carlocs">
      <div className="max-w-xl mx-auto px-5 py-14 md:py-20" data-testid="track-page">
        <div className="text-center mb-10">
          <PackageSearch className="w-10 h-10 text-[#C9A24B] mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl mb-3">Track Your <span className="italic text-[#C9A24B]">Order</span></h1>
          <p className="text-[#F5EFE3]/50 text-sm">Enter your order number and the email you used at checkout.</p>
        </div>

        <form onSubmit={track} className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 space-y-4 mb-8">
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
            placeholder="Order number e.g. CC-0001"
            required
            data-testid="track-order-number"
            className="w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#C9A24B] transition-colors"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used at checkout"
            required
            data-testid="track-email"
            className="w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#C9A24B] transition-colors"
          />
          {error && <p className="text-red-400 text-sm" data-testid="track-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="track-submit-btn"
            className="w-full bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
          >
            {busy ? "Searching…" : "Track Order"}
          </button>
        </form>

        {order && (
          <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 md:p-8" data-testid="track-result">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#C9A24B]/10">
              <p className="font-serif text-2xl text-[#C9A24B]">{order.order_number}</p>
              <span className="inline-flex items-center gap-1.5 text-[#C9A24B] text-xs uppercase tracking-widest">
                <CheckCircle2 className="w-4 h-4" /> {STATUS_LABELS[order.status] || order.status}
              </span>
            </div>
            {order.has_made_to_order && (
              <p className="border border-[#C9A24B]/40 bg-[#C9A24B]/10 text-[#C9A24B] text-xs leading-relaxed rounded-xl px-4 py-3 mb-6">
                Includes made-to-order locs extensions — handmade in {order.lead_time_text || "7-15 days"}, then your delivery begins.
              </p>
            )}
            <div className="mb-6"><OrderItemsList items={order.items} /></div>
            <StatusTimeline order={order} />
          </div>
        )}

        <p className="text-center text-[#F5EFE3]/40 text-sm mt-8">
          Have an account? <Link to="/account/login" className="text-[#C9A24B] underline underline-offset-4" data-testid="track-login-link">Sign in to see all your orders</Link>
        </p>
      </div>
    </ShopLayout>
  );
};

export default TrackOrderPage;
